import { useMemo } from 'react';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useApp } from '@/contexts/AppContext';
import { getPlanLimits, getPlanName, PlanLimits } from '@/lib/plan-limits';
import { startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';

export interface PlanLimitsInfo extends PlanLimits {
  planId: string;
  planName: string;
  disciplinasUsadas: number;
  sessoesMesUsadas: number;
  podeCriarDisciplina: boolean;
  podeCriarSessao: boolean;
  bloqueadas: Set<string>;
}

export function usePlanLimits(): PlanLimitsInfo {
  const { currentPlan, subscribed } = useSubscription();
  const { disciplinas, sessoes } = useApp();

  return useMemo(() => {
    const planId = subscribed && currentPlan ? currentPlan.id : 'free';
    const limits = getPlanLimits(planId);
    const planName = getPlanName(planId);

    const disciplinasUsadas = disciplinas.length;

    // Count sessions in current month
    const now = new Date();
    const mesInicio = startOfMonth(now);
    const mesFim = endOfMonth(now);
    const sessoesMesUsadas = sessoes.filter(s =>
      isWithinInterval(s.data, { start: mesInicio, end: mesFim })
    ).length;

    const podeCriarDisciplina = limits.maxDisciplinas === null || disciplinasUsadas < limits.maxDisciplinas;
    const podeCriarSessao = limits.maxSessoesMes === null || sessoesMesUsadas < limits.maxSessoesMes;

    const bloqueadas = new Set<string>();
    if (limits.maxDisciplinas !== null) {
      const ativas = disciplinas
        .filter(d => d.ativo)
        .sort((a, b) => a.criadoEm.getTime() - b.criadoEm.getTime());
      ativas.slice(limits.maxDisciplinas).forEach(d => bloqueadas.add(d.id));
      disciplinas.filter(d => !d.ativo).forEach(d => bloqueadas.add(d.id));
    }

    return {
      ...limits,
      planId,
      planName,
      disciplinasUsadas,
      sessoesMesUsadas,
      podeCriarDisciplina,
      podeCriarSessao,
      bloqueadas,
    };
  }, [currentPlan, subscribed, disciplinas, sessoes]);
}
